import React, { useState, useMemo } from 'react';
import { 
  Building2, 
  MapPin, 
  Calendar, 
  MoreHorizontal, 
  Search,
  Filter,
  CheckCircle2,
  XCircle,
  Clock,
  ExternalLink
} from 'lucide-react';

export type TenantRequestStatus = 'pending' | 'approved' | 'rejected' | 'all';

export interface TenantRequest {
  id: string;
  businessName: string;
  category: string;
  city: string;
  state: string;
  requestDate: string;
  status: 'pending' | 'approved' | 'rejected';
  contactName: string;
  email: string;
}

interface TenantRequestListProps {
  requests: TenantRequest[];
  onAction?: (id: string, action: 'approve' | 'reject' | 'view') => void;
}

export const TenantRequestList: React.FC<TenantRequestListProps> = ({ requests, onAction }) => {
  const [filter, setFilter] = useState<TenantRequestStatus>('all');
  const [search, setSearch] = useState('');

  const filteredRequests = useMemo(() => {
    return requests.filter(req => {
      const matchesStatus = filter === 'all' || req.status === filter;
      const matchesSearch = 
        req.businessName.toLowerCase().includes(search.toLowerCase()) ||
        req.email.toLowerCase().includes(search.toLowerCase()) ||
        req.city.toLowerCase().includes(search.toLowerCase());
      
      return matchesStatus && matchesSearch;
    });
  }, [requests, filter, search]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'bg-green-50 text-green-700 ring-green-600/20 dark:bg-green-900/30 dark:text-green-400';
      case 'rejected': return 'bg-red-50 text-red-700 ring-red-600/20 dark:bg-red-900/30 dark:text-red-400';
      default: return 'bg-yellow-50 text-yellow-700 ring-yellow-600/20 dark:bg-yellow-900/30 dark:text-yellow-400';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved': return <CheckCircle2 className="size-4" />;
      case 'rejected': return <XCircle className="size-4" />;
      default: return <Clock className="size-4" />;
    }
  };

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm overflow-hidden">
      {/* Header & Filters */}
      <div className="p-4 sm:p-6 border-b border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row gap-4 justify-between items-center">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
          <Building2 className="size-5 text-slate-500" />
          Solicitações de Cadastro
        </h2>

        <div className="flex w-full sm:w-auto items-center gap-2">
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-slate-400" />
            <input 
              type="text" 
              placeholder="Buscar empresa, email..." 
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm rounded-lg border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>
          
          <select 
            value={filter}
            onChange={(e) => setFilter(e.target.value as TenantRequestStatus)}
            className="px-3 py-2 text-sm rounded-lg border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Todos</option>
            <option value="pending">Pendentes</option>
            <option value="approved">Aprovados</option>
            <option value="rejected">Rejeitados</option>
          </select>
        </div>
      </div>

      {/* List */}
      <div className="divide-y divide-slate-200 dark:divide-slate-800">
        {filteredRequests.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            Nenhuma solicitação encontrada.
          </div>
        ) : (
          filteredRequests.map((req) => (
            <div key={req.id} className="p-4 sm:p-6 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors group">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 truncate">
                      {req.businessName}
                    </h3>
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ${getStatusColor(req.status)}`}>
                      {getStatusIcon(req.status)}
                      {req.status === 'pending' ? 'Pendente' : req.status === 'approved' ? 'Aprovado' : 'Rejeitado'}
                    </span>
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-500 dark:text-slate-400 mt-2">
                    <div className="flex items-center gap-1.5">
                      <Building2 className="size-4" />
                      {req.category}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <MapPin className="size-4" />
                      {req.city}, {req.state}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Calendar className="size-4" />
                      {req.requestDate}
                    </div>
                  </div>

                  <div className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                    <span className="font-medium text-slate-700 dark:text-slate-200">Contato:</span> {req.contactName} ({req.email})
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => onAction?.(req.id, 'view')}
                    className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                    title="Ver detalhes"
                  >
                    <ExternalLink className="size-5" />
                  </button>
                  <button className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                    <MoreHorizontal className="size-5" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer / Pagination (simplified) */}
      <div className="p-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 flex justify-center">
        <button className="text-sm font-medium text-blue-600 hover:text-blue-700">
          Carregar mais
        </button>
      </div>
    </div>
  );
};
